// "Import from Mermaid" modal — paste a Mermaid sequenceDiagram, get a
// preview of the converted ArcPilot flow grammar, then import it into
// the current session.
//
// Three-step flow inside the modal:
//   1. Paste / type Mermaid into the top textarea.
//   2. Click "Convert" — calls /api/mermaid/convert. The result lands
//      in the editable preview pane underneath.
//   3. Click "Import" — calls /api/sessions/:id/import-from-mermaid
//      with the (possibly edited) flowText. 409 with conflict info →
//      destructive-overwrite confirm path, mirroring ImportFromDocument.
//
// The convert + import calls are separate so the user can tweak the
// generated grammar before committing.

import React, { useEffect, useRef, useState } from 'react';
import '../styles/ImportFromDocumentModal.css';

import type { DocImportResult } from '../hooks/sessionTypes';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string | null;
  // Save callback wired through the session hook so newModel refreshes
  // after a successful import.
  onImport: (args: { flowText: string; force?: boolean }) => Promise<DocImportResult>;
}

interface ConvertResponse {
  flowText: string;
  errors?: string[];
}

const SAMPLE = `sequenceDiagram
  participant App as Customer App
  participant PR as Payment Rails
  participant BL as Balance & Ledger
  App->>PR: TopUp
  PR->>BL: TopUpPaid
  BL->>App: receipt + new balance`;

const MermaidImportModal: React.FC<Props> = ({ isOpen, onClose, sessionId, onImport }) => {
  const [mermaid, setMermaid] = useState('');
  const [flowText, setFlowText] = useState('');
  const [converting, setConverting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [convertErrors, setConvertErrors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ existingStatementCount: number } | null>(null);
  const mermaidRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setMermaid('');
    setFlowText('');
    setConverting(false);
    setImporting(false);
    setConvertErrors([]);
    setError(null);
    setConflict(null);
    const t = setTimeout(() => mermaidRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  const handleConvert = async () => {
    const body = mermaid.trim();
    if (!body) return;
    setConverting(true);
    setError(null);
    setConvertErrors([]);
    setFlowText('');
    try {
      const res = await fetch('/api/mermaid/convert', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mermaid: body }),
      });
      const data: ConvertResponse & { error?: string } = await res.json();
      if (!res.ok) {
        setError(data?.error || `Conversion failed (${res.status}).`);
        return;
      }
      setFlowText(data.flowText || '');
      setConvertErrors(data.errors || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setConverting(false);
    }
  };

  const handleImport = async (force = false) => {
    if (!sessionId) {
      setError('No active session — start a session before importing.');
      return;
    }
    const body = flowText.trim();
    if (!body) {
      setError('Nothing to import — convert the Mermaid diagram first.');
      return;
    }
    setImporting(true);
    setError(null);
    setConflict(null);
    try {
      const result = await onImport({ flowText: body, force });
      if (!result.ok) {
        if (result.status === 409 && result.conflict) {
          setConflict(result.conflict);
        } else {
          setError(result.error || 'Import failed.');
        }
        return;
      }
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setImporting(false);
    }
  };

  const insertSample = () => {
    setMermaid(SAMPLE);
    setFlowText('');
    setConvertErrors([]);
    setError(null);
  };

  if (!isOpen) return null;

  const busy = converting || importing;

  return (
    <div
      className="ifd-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="ifd-modal" role="dialog" aria-modal="true" aria-labelledby="mmd-title">
        <header className="ifd-head">
          <h2 id="mmd-title">Import from Mermaid</h2>
          <button className="ifd-close" onClick={onClose} disabled={busy} aria-label="Close">×</button>
        </header>

        <div className="ifd-body">
          <p className="ifd-blurb">
            Paste a Mermaid <code>sequenceDiagram</code>. The model maps participants to actors and
            arrows to flow statements. You can edit the preview before importing.
          </p>

          <label className="ifd-label" htmlFor="mmd-input">Mermaid source</label>
          <textarea
            id="mmd-input"
            ref={mermaidRef}
            className="ifd-textarea"
            value={mermaid}
            onChange={(e) => setMermaid(e.target.value)}
            placeholder="sequenceDiagram&#10;  participant App as Customer App&#10;  participant Booking&#10;  App->>Booking: search flights"
            rows={10}
            disabled={busy}
            spellCheck={false}
          />

          <div className="ifd-actions-inline">
            <button
              type="button"
              className="ifd-btn ifd-btn--ghost"
              onClick={insertSample}
              disabled={busy}
            >
              Insert sample
            </button>
            <button
              type="button"
              className="ifd-btn ifd-btn--primary"
              onClick={handleConvert}
              disabled={busy || !mermaid.trim()}
            >
              {converting ? 'Converting…' : 'Convert →'}
            </button>
          </div>

          {convertErrors.length > 0 && (
            <div className="ifd-warn">
              {convertErrors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}

          {flowText && (
            <>
              <label className="ifd-label" htmlFor="mmd-preview">ArcPilot flow grammar (editable)</label>
              <textarea
                id="mmd-preview"
                className="ifd-textarea ifd-textarea--mono"
                value={flowText}
                onChange={(e) => setFlowText(e.target.value)}
                rows={12}
                disabled={busy}
                spellCheck={false}
              />
            </>
          )}

          {error && <div className="ifd-error" role="alert">{error}</div>}

          {conflict && (
            <div className="ifd-conflict">
              <p>
                This session already has <strong>{conflict.existingStatementCount}</strong>{' '}
                statements. Importing will replace the existing flow.
              </p>
              <div className="ifd-conflict-actions">
                <button
                  type="button"
                  className="ifd-btn ifd-btn--ghost"
                  onClick={() => setConflict(null)}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ifd-btn ifd-btn--danger"
                  onClick={() => handleImport(true)}
                  disabled={busy}
                >
                  {importing ? 'Importing…' : 'Overwrite and import'}
                </button>
              </div>
            </div>
          )}
        </div>

        <footer className="ifd-foot">
          <button type="button" className="ifd-btn ifd-btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="ifd-btn ifd-btn--primary"
            onClick={() => handleImport(false)}
            disabled={busy || !flowText.trim() || !sessionId}
            title={!sessionId ? 'Start a session before importing' : ''}
          >
            {importing ? 'Importing…' : 'Import into session'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default MermaidImportModal;
